import React, { FC, useState, useRef, useEffect } from 'react';
import { ReactComponent as ChevronDownIcon } from 'src/assets/images/onboarding-trainer/dashboard/profile/packages/create-edit-package/dropdown-chevron.svg';
import './CustomSelect.scss';

export interface CustomSelectOption {
  label: string;
  value: string;
}

interface CustomSelectProps {
  id?: string;
  value: string;
  options: (string | CustomSelectOption)[];
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  variant?: 'input' | 'pill';
  fullWidth?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  className?: string;
  isEditable?: boolean;
}

export const CustomSelect: FC<CustomSelectProps> = ({
  id,
  value,
  options,
  onChange,
  onBlur,
  placeholder = 'Select option',
  hasError = false,
  disabled = false,
  variant = 'input',
  fullWidth = false,
  ariaLabel,
  ariaDescribedBy,
  className = '',
  isEditable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Normalize options
  const normalizedOptions: CustomSelectOption[] = options.map((opt) =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt,
  );

  const selectedOption = normalizedOptions.find((opt) => opt.value === value);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isOpen]);

  // Keyboard accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter') {
      if (!isEditable) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === ' ' && !isEditable) {
      e.preventDefault();
      setIsOpen((prev) => !prev);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const currentIndex = normalizedOptions.findIndex((opt) => opt.value === value);
        const nextIndex = (currentIndex + 1) % normalizedOptions.length;
        onChange(normalizedOptions[nextIndex].value);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const currentIndex = normalizedOptions.findIndex((opt) => opt.value === value);
        const prevIndex = (currentIndex - 1 + normalizedOptions.length) % normalizedOptions.length;
        onChange(normalizedOptions[prevIndex].value);
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={`custom-select-root variant-${variant} ${fullWidth ? 'full-width' : ''} ${
        isOpen ? 'is-open' : ''
      } ${hasError ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''} ${
        isEditable ? 'is-editable-root' : ''
      } ${className}`}
    >
      {isEditable ? (
        <div
          className={`custom-select-trigger is-editable ${!value ? 'is-placeholder' : ''}`}
          onClick={() => {
            if (!disabled) {
              inputRef.current?.focus();
              setIsOpen(true);
            }
          }}
        >
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="trigger-editable-input"
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedBy}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            onChange={(e) => {
              onChange(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => {
              if (!disabled) setIsOpen(true);
            }}
            onBlur={(e) => {
              if (onBlur) {
                onBlur(e.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            className="chevron-wrapper"
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) setIsOpen((prev) => !prev);
            }}
            aria-label="Toggle options"
            tabIndex={-1}
            disabled={disabled}
          >
            <ChevronDownIcon className={`chevron-icon ${isOpen ? 'rotate' : ''}`} />
          </button>
        </div>
      ) : (
        <button
          id={id}
          type="button"
          className={`custom-select-trigger ${!value ? 'is-placeholder' : ''}`}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
        >
          <span className="trigger-label-text">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="chevron-wrapper">
            <ChevronDownIcon className={`chevron-icon ${isOpen ? 'rotate' : ''}`} />
          </div>
        </button>
      )}

      {isOpen && (
        <div className="custom-dropdown-popup" role="listbox" aria-label={ariaLabel}>
          {normalizedOptions.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={`custom-dropdown-option ${isSelected ? 'is-selected' : ''}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt.value);
                  setIsOpen(false);
                }}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span className="option-label">{opt.label}</span>
                {isSelected && (
                  <svg
                    className="selected-check-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ffa100"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
